/**
 * NB-F8 + NB-F9: client-side observability boot. Initializes PostHog
 * (analytics, consent-gated) and Sentry (error reporting, opt-out
 * respected) once at app mount. Renders nothing.
 *
 * Re-runs init when the user changes consent via the CookieBanner,
 * so flipping from essential-only to all/analytics turns PostHog on
 * without a page reload.
 */
'use client'

import { useEffect } from 'react'
import {
  init as initPostHog,
  identifyUser as identifyPostHog,
  resetUser as resetPostHog,
  optOut as optOutPostHog,
} from '@/lib/posthog'
import { init as initSentry, setUser as setSentryUser } from '@/lib/sentry'
import { getStoredUser } from '@/lib/auth'
import { readConsent } from '@/components/CookieBanner'

export default function ObservabilityBoot() {
  useEffect(() => {
    const identifyFromStorage = async () => {
      // Fire-and-forget. Each lib's init() is idempotent + lazy-loads
      // its SDK only when the env vars + consent state align.
      await initPostHog()
      await initSentry()
      // Once the SDKs are initialized, identify the user if logged in.
      // PostHog activation funnel needs the user identity to compute
      // signup → first-company → first-briefing cross-session funnels.
      // Sentry uses the user id to group errors by account for triage.
      const user = getStoredUser()
      if (user) {
        identifyPostHog(user.id, { email: user.email, full_name: user.full_name })
        setSentryUser({ id: user.id, email: user.email })
      } else {
        // Anonymous session: clear any prior identity (logout case).
        resetPostHog()
        setSentryUser(null)
      }
    }
    void identifyFromStorage()

    const onConsentChange = () => {
      // GDPR-critical: if user flips to 'essential' (analytics-off),
      // stop capturing immediately + clear the device identifier.
      const newConsent = readConsent()
      if (newConsent === 'essential') {
        optOutPostHog()
        setSentryUser(null)
      }
      void identifyFromStorage()
    }
    // Listen for explicit auth-change events (login / logout) so the
    // identity re-syncs without a page reload.
    const onAuthChange = () => {
      void identifyFromStorage()
    }
    window.addEventListener('ps:consent', onConsentChange)
    window.addEventListener('ps:auth-change', onAuthChange)
    return () => {
      window.removeEventListener('ps:consent', onConsentChange)
      window.removeEventListener('ps:auth-change', onAuthChange)
    }
  }, [])

  return null
}
